import { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/shared/components/Loading';
import { useReferralSettings, useUpdateReferralSettings } from '@/services/referrals';
import { ReferralSettings } from '@/models/referral';

const SettingsTab = () => {
    const { data, isLoading } = useReferralSettings();
    const mutation = useUpdateReferralSettings();
    const [draft, setDraft] = useState<Partial<ReferralSettings> | null>(null);

    useEffect(() => {
        if (data?.data) setDraft(data.data);
    }, [data]);

    if (isLoading || !draft) return <LoadingSpinner />;

    const update = <K extends keyof ReferralSettings>(key: K, value: ReferralSettings[K]) => {
        setDraft((prev) => ({ ...(prev || {}), [key]: value }));
    };

    const submit = async () => {
        try {
            const resp = await mutation.mutateAsync(draft);
            if (resp.status === 'error') {
                window.alert(resp.error?.message || 'Failed to save');
                return;
            }
            window.alert('Settings updated.');
        } catch (err) {
            window.alert(err instanceof Error ? err.message : 'Failed to save');
        }
    };

    return (
        <div>
            <p className="text-muted">
                These settings affect commission calculation and qualification timing across the entire program. Changing the qualification window
                affects all pending commissions globally.
            </p>
            <div className="row g-3 mb-3">
                <div className="col-md-4">
                    <label className="form-label">Qualification window (days)</label>
                    <input
                        type="number"
                        min={1}
                        max={365}
                        className="form-control"
                        value={draft.qualificationDays ?? 30}
                        onChange={(e) => update('qualificationDays', Number(e.target.value))}
                    />
                    <div className="form-text">Matches Harmonea's refund policy (default 30).</div>
                </div>
                <div className="col-md-4">
                    <label className="form-label">Attribution cookie (days)</label>
                    <input
                        type="number"
                        min={1}
                        max={365}
                        className="form-control"
                        value={draft.attributionDays ?? 30}
                        onChange={(e) => update('attributionDays', Number(e.target.value))}
                    />
                </div>
                <div className="col-md-4">
                    <label className="form-label">Late attribution window (hours)</label>
                    <input
                        type="number"
                        min={0}
                        max={720}
                        className="form-control"
                        value={draft.lateAttributionHours ?? 48}
                        onChange={(e) => update('lateAttributionHours', Number(e.target.value))}
                    />
                </div>
            </div>

            <h5 className="mt-4">Tier thresholds (qualified sign-ups)</h5>
            <div className="row g-3 mb-3">
                <div className="col-md-6">
                    <label className="form-label">Tier 2 threshold</label>
                    <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={draft.tier2Threshold ?? 3}
                        onChange={(e) => update('tier2Threshold', Number(e.target.value))}
                    />
                </div>
                <div className="col-md-6">
                    <label className="form-label">Tier 3 threshold</label>
                    <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={draft.tier3Threshold ?? 10}
                        onChange={(e) => update('tier3Threshold', Number(e.target.value))}
                    />
                </div>
            </div>

            <h5 className="mt-4">Referrer reward (discount)</h5>
            <p className="text-muted small mb-2">
                The active reward. When a referral qualifies, the referrer is minted a single-use Stripe promo code for the discount below.
            </p>
            <div className="row g-3 mb-3">
                <div className="col-md-6">
                    <label className="form-label">Discount on next payment (%)</label>
                    <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        className="form-control"
                        value={draft.referrerDiscountPercent ?? 0}
                        onChange={(e) => update('referrerDiscountPercent', Number(e.target.value))}
                    />
                    <div className="form-text">
                        The referrer gets this % off their next payment when a referral qualifies. Delivered as a single-use Stripe promo code. Set 0
                        to disable.
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="form-label">Reward code valid for (days)</label>
                    <input
                        type="number"
                        min={1}
                        max={365}
                        className="form-control"
                        value={draft.referrerDiscountExpiryDays ?? 30}
                        onChange={(e) => update('referrerDiscountExpiryDays', Number(e.target.value))}
                    />
                </div>
            </div>

            <h5 className="mt-4">Commission rates (%)</h5>
            <div className="row g-3 mb-1">
                <div className="col-md-4">
                    <label className="form-label">Tier 1 rate</label>
                    <input
                        type="number"
                        min={0}
                        max={100}
                        step="0.1"
                        className="form-control"
                        value={draft.tier1RatePercent ?? 10}
                        onChange={(e) => update('tier1RatePercent', Number(e.target.value))}
                    />
                </div>
                <div className="col-md-4">
                    <label className="form-label">Tier 2 rate</label>
                    <input
                        type="number"
                        min={0}
                        max={100}
                        step="0.1"
                        className="form-control"
                        value={draft.tier2RatePercent ?? 20}
                        onChange={(e) => update('tier2RatePercent', Number(e.target.value))}
                    />
                </div>
                <div className="col-md-4">
                    <label className="form-label">Tier 3 rate</label>
                    <input
                        type="number"
                        min={0}
                        max={100}
                        step="0.1"
                        className="form-control"
                        value={draft.tier3RatePercent ?? 30}
                        onChange={(e) => update('tier3RatePercent', Number(e.target.value))}
                    />
                </div>
            </div>
            <p className="text-muted small mb-3">Legacy — cash commissions are only paid when the discount above is 0.</p>

            <div className="form-check form-switch mt-3">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="blockSelfReferral"
                    checked={!!draft.blockSelfReferral}
                    onChange={(e) => update('blockSelfReferral', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="blockSelfReferral">
                    Block self-referrals (email + IP match)
                </label>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
                <button type="button" className="btn btn-primary" disabled={mutation.isPending} onClick={submit}>
                    {mutation.isPending ? 'Saving…' : 'Save changes'}
                </button>
            </div>

            {data?.data?.lastUpdatedAt && (
                <div className="text-muted small mt-2">
                    Last updated {new Date(data.data.lastUpdatedAt).toLocaleString()}
                    {data.data.lastUpdatedBy ? ` by ${data.data.lastUpdatedBy}` : ''}
                </div>
            )}
        </div>
    );
};

export default SettingsTab;
